import { Button, ButtonLink, cn } from "@mandrac/ui";
import { ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Error state for a failed query, shown instead of an endless spinner. 403 says the data is not
 * available to the user (no retry, it would fail again); 401 offers the login page; anything else
 * shows the API's message and a retry button.
 */
export function QueryError({ query, className }: { query: { error: unknown; refetch: () => unknown }; className?: string }) {
  const { t, te } = useI18n();
  const e = query.error instanceof ApiError ? query.error : null;
  const forbidden = e?.status === 403;
  const unauthorized = e?.status === 401;
  const detail = forbidden ? null : unauthorized ? t("error.unauthorized") : te(e?.message);
  return (
    <div role="alert" data-testid="query-error" className={cn("flex flex-col items-start gap-2 rounded-panel border border-danger bg-danger-tint px-4 py-3 text-14 text-danger-text", className)}>
      <p className="m-0 font-semibold">{forbidden ? t("error.forbiddenData") : t("error.loadFailed")}</p>
      {detail ? <p className="m-0">{detail}</p> : null}
      {e?.requestId ? <p className="m-0 text-13">{t("error.requestId", { id: e.requestId })}</p> : null}
      {unauthorized ? (
        <ButtonLink size="sm" href="/login">
          {t("login.title")}
        </ButtonLink>
      ) : forbidden ? null : (
        <Button size="sm" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </Button>
      )}
    </div>
  );
}
